import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { ResumeStepValidatable } from '@edtech-ui/core/models/resume-builder/step-validators';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-resumer-builder-projects',
	standalone: true,
	imports: [ReactiveFormsModule, CommonModule, HasPermissionDirective],
	templateUrl: './resumer-builder-projects.component.html',
	styleUrl: './resumer-builder-projects.component.scss'
})
export class ResumerBuilderProjectsComponent implements OnInit, OnDestroy ,ResumeStepValidatable {

	userProfileForm: FormGroup;
	subscription = new Subscription();
	aiLoadingIndex: number | null = null;
	PermissionForStudents = PermissionForStudents;

	constructor(
		private resumeService: ResumeService,
		private _toastr: ToastrService,
		private route: ActivatedRoute
	) {
		this.userProfileForm = new FormGroup({
			projects: new FormArray([this.createProjectForm()])
		});
	}


	ngOnInit(): void {
		const resumeId = this.route.snapshot.paramMap.get('resumeId');
		if (resumeId) {
			this.resumeService.getFullResumeData().subscribe(data => {
				if (data?.projects) {
					this.editProjectsData(data.projects);
				}
			});
			return;
		}

		const data = this.resumeService.getSectionData('projects');

		if (data && data.length) {
			this.editProjectsData(data);
		}
	}


	createProjectForm(): FormGroup {
		return new FormGroup({
			title: new FormControl('', Validators.required),
			description: new FormControl('', Validators.required),
			technologies: new FormControl(''),
			outcome: new FormControl('')
		});
	}

	get projects(): FormArray {
		return this.userProfileForm.get('projects') as FormArray;
	}


	addProject() {
		this.projects.push(this.createProjectForm());
	}


	removeProject(index: number) {
		this.projects.removeAt(index);
	}


	generateDescription(index: number) {
		const project = this.projects.at(index);

		const title = project.get('title')?.value || 'a project';
		const tools = project.get('technologies')?.value || 'modern technologies';

		const generated = `Developed ${title} using ${tools}. Focused on solving real-world problems with optimized performance and user-friendly design.`;

		project.get('description')?.setValue(generated);
	}


	saveData() {
		const payload = this.projects.value.map((project: any) => ({
			...project,
			technologies: Array.isArray(project.technologies)
				? project.technologies
				: project.technologies
					? project.technologies.split(',').map((t: string) => t.trim())
					: []
		}));
		this.resumeService.setSectionData('projects', payload);
	}


	postAiDescription(index: number) {
		this.aiLoadingIndex = index;

		const project = this.projects.at(index);
		const description = project.get('description')?.value;

		if (!description) {
			this._toastr.warning('Please enter description first');
			return;
		}

		const payload = {
			description: description
		}
		this.subscription.add(
			this.resumeService.postAiDescriptionForProject(payload).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success(response.Message);
						project.get('description')?.setValue(response.Data.refined_description);
					}
				}),
				finalize(() => {
					this.aiLoadingIndex = null;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong. Please try again');
					return of(null);
				})
			).subscribe()
		)
	}


	editProjectsData(data: any[]) {
		if (!data || !data.length) return;
		this.projects.clear();
		data.forEach((proj: any) => {
			const group = this.createProjectForm();
			group.patchValue({
				title: proj.title || '',
				description: proj.description || '',
				technologies: Array.isArray(proj.technologies)
					? proj.technologies.join(', ')
					: proj.technologies || '',
				outcome: proj.outcome || ''
			});
			this.projects.push(group);
		});
	}

	
	getControl(index: number, field: string) {
		return this.projects.at(index).get(field);
	}

	
	isFormValid(): boolean {
		return this.userProfileForm.valid;
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
